import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { Icon } from '@/components/Icon';
import { columnCount } from './switcher';
import { installKeyboardLock } from './keyboardLock';
import { useAltTabStore } from './store';
import css from './AltTab.module.css';

/**
 * The Alt+Tab box.
 *
 * A grid of icons, the highlighted one in a blue square, and its caption
 * underneath. Hold Alt and press Tab to walk forwards, Shift+Tab to walk back,
 * arrows to move about, Escape to give up; let Alt go and the highlighted
 * window comes to the front, restored if it was minimized.
 *
 * ── The second binding ──────────────────────────────────────────────────────
 * Alt+` does everything Alt+Tab does, because Alt+Tab is not the page's key to
 * take: on Windows the OS switches applications long before the browser is
 * asked. `keyboardLock.ts` wins it back while the page is fullscreen, which is
 * the whole of what is possible, and the backquote covers every other case.
 * The precedent is Alt+F4 in `WindowLayer`, which is bound and caught on the
 * same best-effort terms.
 *
 * The pointer works too, as it did in XP: hovering moves the highlight and a
 * click commits, so the box is usable even where no chord survives.
 */
export function AltTab() {
  const open = useAltTabStore((s) => s.open);
  const items = useAltTabStore((s) => s.items);
  const index = useAltTabStore((s) => s.index);

  useEffect(() => installKeyboardLock(), []);

  useEffect(() => {
    const store = () => useAltTabStore.getState();

    const onKeyDown = (event: KeyboardEvent) => {
      const state = store();

      if (state.open && event.key === 'Escape') {
        event.preventDefault();
        state.cancel();
        return;
      }

      // Arrows walk the grid, but only while the box is up — out on the
      // desktop an arrow key moves the icon selection, and that stays true.
      if (state.open) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          state.cycle(true);
          return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          state.cycle(false);
          return;
        }
      }

      if (!event.altKey || event.ctrlKey || event.metaKey) return;
      // `code` rather than `key`: with Alt held the backquote reports as a
      // dead key on some layouts, and the physical key is what was pressed.
      if (event.key !== 'Tab' && event.code !== 'Backquote') return;

      event.preventDefault();
      if (state.open) state.cycle(event.shiftKey);
      else state.begin(event.shiftKey);
    };

    // Letting go of Alt is the commit, which is why the box has no OK button.
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Alt' || !event.altKey) store().commit();
    };

    // The page losing focus usually means the *operating system's* Alt+Tab won
    // the race. Whatever happened, the box must not be left up over a desktop
    // nobody is looking at.
    const onBlur = () => store().cancel();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  if (!open || items.length === 0) return null;

  const current = items[index];

  return (
    <div className={css.layer} role="presentation">
      <div
        className={css.box}
        style={{ '--alt-tab-columns': columnCount(items.length) } as CSSProperties}
        role="listbox"
        aria-label="Switch window"
      >
        <div className={css.grid}>
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={i === index}
              className={css.cell}
              data-selected={i === index ? '' : undefined}
              // Pointer rather than mouse: the highlight should follow a
              // finger dragged across the box as well as a pointer.
              onPointerEnter={() => useAltTabStore.getState().select(i)}
              onPointerDown={(event) => {
                event.preventDefault();
                useAltTabStore.getState().select(i);
              }}
              onClick={() => useAltTabStore.getState().commit()}
            >
              <Icon src={item.icon} size={32} alt={item.title} />
            </button>
          ))}
        </div>
        {/* One line, always present even when the caption is empty, so the box
            does not change height as the highlight moves. */}
        <p className={css.caption}>{current?.title ?? ''}</p>
      </div>
    </div>
  );
}
